/* Splits text into Kohd sentences.
   - Nodal words become glyphs; lexicon words become constructs in the gap
     before the next glyph (gaps[0] sits on the coupler trace, gaps[n] after
     the last glyph).
   - "the"/"a"/"an" mark the next word's charge node.
   - "from X to Y" and "if X then Y" place one from-to / if-then symbol
     between X and Y (where "to" / "then" stood).
   - "." "!" "?" end a sentence; "," adds a comma to the next connection. */

import { ARTICLES, Article, ConstructType, LEXICON_PHRASES, LEXICON_WORDS } from './lexicon';

export type Punctuation = 'none' | 'period' | 'exclamation' | 'question';

export type GapItem =
  | { kind: 'construct'; type: ConstructType; plural?: true }
  | { kind: 'comma' };

export interface KohdWord {
  text: string;
  article?: Article;
}

export interface KohdSentence {
  words: KohdWord[];
  gaps: GapItem[][];
  punctuation: Punctuation;
}

const END_MARKS: Record<string, Punctuation> = { '.': 'period', '!': 'exclamation', '?': 'question' };

interface Token {
  word: string;
  comma: boolean;
  end: Punctuation;
}

function tokenize(text: string): Token[] {
  return text
    .split(/\s+/)
    .map((raw) => {
      const trimmed = raw.replace(/^[^\p{L}\p{N}']+/u, '');
      const trail = trimmed.match(/[^\p{L}\p{N}']*$/u)?.[0] ?? '';
      const word = trimmed.slice(0, trimmed.length - trail.length).toLowerCase();
      const endChar = [...trail].reverse().find((c) => c in END_MARKS);
      return { word, comma: trail.includes(','), end: endChar ? END_MARKS[endChar] : 'none' };
    })
    .filter((t) => t.word.length > 0 || t.end !== 'none' || t.comma);
}

function newSentence(): KohdSentence {
  return { words: [], gaps: [[]], punctuation: 'none' };
}

export function parseKohd(text: string): KohdSentence[] {
  const tokens = tokenize(text);
  const sentences: KohdSentence[] = [];
  let current = newSentence();
  let pendingArticle: Article | undefined;
  let pendingPair: 'fromTo' | 'ifThen' | null = null;

  const gap = () => current.gaps[current.gaps.length - 1];
  const addConstruct = (type: ConstructType, plural?: true) =>
    gap().push(plural ? { kind: 'construct', type, plural } : { kind: 'construct', type });
  const laterTokenIs = (from: number, word: string) => {
    for (let j = from; j < tokens.length; j++) {
      if (tokens[j].word === word) return true;
      if (tokens[j].end !== 'none') return false;
    }
    return false;
  };

  for (let i = 0; i < tokens.length; i++) {
    const t = tokens[i];
    const next = tokens[i + 1];
    let last = t;

    if (t.word) {
      const phrase = next && t.comma === false && t.end === 'none' ? LEXICON_PHRASES[`${t.word} ${next.word}`] : undefined;
      if (phrase) {
        addConstruct(phrase.type, phrase.plural);
        last = next;
        i++;
      } else if (t.word in ARTICLES) {
        pendingArticle = ARTICLES[t.word];
      } else if (t.word === 'from' && laterTokenIs(i + 1, 'to')) {
        pendingPair = 'fromTo';
      } else if (t.word === 'if' && laterTokenIs(i + 1, 'then')) {
        pendingPair = 'ifThen';
      } else if ((t.word === 'to' && pendingPair === 'fromTo') || (t.word === 'then' && pendingPair === 'ifThen')) {
        addConstruct(pendingPair);
        pendingPair = null;
      } else if (t.word in LEXICON_WORDS) {
        const entry = LEXICON_WORDS[t.word];
        addConstruct(entry.type, entry.plural);
      } else if (/\p{L}/u.test(t.word)) {
        current.words.push(pendingArticle ? { text: t.word.toUpperCase(), article: pendingArticle } : { text: t.word.toUpperCase() });
        current.gaps.push([]);
        pendingArticle = undefined;
      }
    }

    if (last.comma) gap().push({ kind: 'comma' });
    if (last.end !== 'none') {
      current.punctuation = last.end;
      sentences.push(current);
      current = newSentence();
      pendingArticle = undefined;
      pendingPair = null;
    }
  }

  if (current.words.length > 0 || current.gaps.some((g) => g.length > 0)) sentences.push(current);
  return sentences;
}
